/* eslint-disable max-lines */
/* IMPORTS ------------------------------------------------------------------------------------- */
import { get, writable } from 'svelte/store';
import { deepEqual } from 'fast-equals';
/* UTILITIES ---------------------------------------------------------------------------------- */
// eslint-disable-next-line  max-statements, max-lines-per-function, complexity
const decode = ([key, value]) => {
    if (value === '') {
        return [key, undefined];
    }
    if (value === 'null') {
        return [key, null];
    }
    if (value === 'true' || value === 'false') {
        return [key, value === 'true'];
    }
    if (key.endsWith('[]') && value === '') {
        return [key.replace('[]', ''), []];
    }
    if (key.endsWith('[]') && value !== '') {
        return [key.replace('[]', ''), value.split('|')];
    }
    if (!isNaN(Number(value)) && value !== '') {
        return [key, Number(value)];
    }
    try {
        return [key, JSON.parse(value)];
    }
    catch (error) {
        return [key, value];
    }
};
// eslint-disable-next-line  max-statements, max-lines-per-function, complexity
const encode = ([key, value]) => {
    if (value === undefined) {
        return [key, ''];
    }
    if (value === null) {
        return [key, 'null'];
    }
    if (typeof value === 'boolean') {
        return [key, value ? 'true' : 'false'];
    }
    if (typeof value === 'number') {
        return [key, String(value)];
    }
    if (Array.isArray(value)) {
        return [`${key}[]`, value.join('|')];
    }
    if (typeof value === 'object') {
        return [key, JSON.stringify(value)];
    }
    if (typeof value === 'string') {
        return [key, value];
    }
    return [key, String(value)];
};
const getScopedParametersFromUrl = () => {
    const URL_PARAMS = [...new URLSearchParams(window.location.search)].map(decode);
    const PARAMS = {};
    if (URL_PARAMS.length === 0) {
        return null;
    }
    URL_PARAMS.forEach(([key, value]) => {
        const [SCOPE, KEY] = key.includes('.') ? key.split('.') : [undefined, key];
        if (SCOPE !== undefined) {
            if (PARAMS[SCOPE] === undefined) {
                PARAMS[SCOPE] = {};
            }
            PARAMS[SCOPE][KEY] = value;
            return;
        }
        PARAMS[KEY] = value;
    });
    return PARAMS;
};
const getUrlFromParameters = (parameters, scopes) => {
    const QUERY = new URLSearchParams();
    const SCOPED_PARAMETERS = Object.entries(parameters ?? {}).filter(([, data]) => typeof data === 'object');
    const UNSCOPED_PARAMETERS = Object.entries(parameters ?? {}).filter(([, data]) => typeof data !== 'object');
    for (const [SCOPE, DATA] of SCOPED_PARAMETERS) {
        if (scopes.includes(SCOPE)) {
            // eslint-disable-next-line max-depth
            for (const [KEY, VALUE] of Object.entries(DATA)) {
                QUERY.set(...encode([`${SCOPE}.${KEY}`, VALUE]));
            }
        }
    }
    for (const [KEY, VALUE] of UNSCOPED_PARAMETERS) {
        QUERY.set(...encode([KEY, VALUE]));
    }
    if (QUERY.size === 0) {
        return '';
    }
    return `?${QUERY.toString()}${window.location.hash}`;
};
const getScope = (STORE, scope) => {
    const SCOPES = get(STORE);
    if (SCOPES === null) {
        return null;
    }
    if (SCOPES[scope] === undefined) {
        return null;
    }
    if (typeof SCOPES[scope] !== 'object') {
        return null;
    }
    if (Object.keys(SCOPES[scope]).length === 0) {
        return null;
    }
    return SCOPES[scope];
};
const getParameter = (STORE, key, scope = null) => {
    const SCOPE = scope === null ? get(STORE) : getScope(STORE, scope);
    if (SCOPE === null) {
        return;
    }
    if (SCOPE[key] === undefined) {
        return;
    }
    // eslint-disable-next-line consistent-return
    return SCOPE[key];
};
const removeParameter = (STORE, key, scope) => {
    STORE.update(() => {
        const SCOPE = scope === undefined ? get(STORE) : getScope(STORE, scope);
        if (SCOPE !== null) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete SCOPE[key];
        }
        return { ...SCOPE };
    });
};
const isSubSetOf = (scope, data) => {
    if (deepEqual(data, scope)) {
        return true;
    }
    if (data === null || scope === null) {
        return false;
    }
    for (const [KEY, VALUE] of Object.entries(data)) {
        if (scope[KEY] !== VALUE) {
            return false;
        }
    }
    return true;
};
const updateScope = (STORE, data, scope) => {
    const SCOPE = getScope(STORE, scope);
    if (isSubSetOf(SCOPE, data)) {
        return;
    }
    STORE.update((store) => {
        if (store === null) {
            return { [scope]: { ...SCOPE, ...data } };
        }
        return { ...store, [scope]: { ...SCOPE, ...data } };
    });
};
const setParameter = (STORE, key, value, scope) => {
    const VALUE = getParameter(STORE, key, scope);
    if (VALUE === value) {
        return;
    }
    if (scope === undefined) {
        STORE.update((store) => ({ ...store, [key]: value }));
        return;
    }
    updateScope(STORE, { [key]: value }, scope);
};
const subscribeParameter = (STORE, key, callback, scope) => {
    let previous = null;
    const unsubscribe = STORE.subscribe(() => {
        const PARAMETER = getParameter(STORE, key, scope);
        if (previous === PARAMETER) {
            return;
        }
        previous = PARAMETER;
        callback(PARAMETER);
    });
    return unsubscribe;
};
const subscribeScope = (STORE, scope, callback) => {
    let previous = null;
    const unsubscribe = STORE.subscribe(() => {
        const SCOPE = getScope(STORE, scope);
        if (deepEqual(previous, SCOPE)) {
            return;
        }
        previous = SCOPE;
        callback(SCOPE);
    });
    return unsubscribe;
};
// eslint-disable-next-line max-statements, max-lines-per-function, complexity
const updateUrl = (store, scopes) => {
    const URL = getUrlFromParameters(store, scopes);
    if (URL === window.location.search) {
        return;
    }
    // If the URL is empty, we need to clear the parameters
    if (URL === '') {
        window.history.replaceState(null, '', window.location.href.replace(window.location.search, ''));
        return;
    }
    // We are introducing a new scope in the URL - we need to do this silently
    if (!scopes.every(scope => window.location.search.includes(`${scope}.`))) {
        window.history.replaceState(null, '', URL);
        return;
    }
    window.history.pushState(null, '', URL);
};
const registerScope = (SCOPE_LIST, scope) => {
    if (get(SCOPE_LIST).includes(scope)) {
        return;
    }
    SCOPE_LIST.update((scopes) => [...new Set([...scopes, scope])]);
};
const unregisterScope = (SCOPE_LIST, scope) => {
    if (!get(SCOPE_LIST).includes(scope)) {
        return;
    }
    SCOPE_LIST.update((scopes) => scopes.filter(key => key !== scope));
};
/* EXPORT -------------------------------------------------------------------------------------- */
// eslint-disable-next-line max-lines-per-function
const scopedQueryState = () => {
    const STORE = writable(getScopedParametersFromUrl());
    const SCOPE_LIST = writable([]);
    STORE.subscribe((store) => {
        if (store === null) {
            return;
        }
        updateUrl(store, get(SCOPE_LIST));
    });
    SCOPE_LIST.subscribe((scopes) => {
        updateUrl(get(STORE), scopes);
    });
    // eslint-disable-next-line max-lines-per-function, max-statements
    window.addEventListener('popstate', (event) => {
        const SCOPES = get(SCOPE_LIST);
        const SCOPE = getScopedParametersFromUrl();
        // Hash Change event
        if (event.state === null) {
            return;
        }
        // This is a hacky fix - when a user lands on '/' they are stuck on an empty page.
        if (window.location.pathname === '/') {
            window.history.back();
        }
        if (SCOPE === null) {
            return;
        }
        if (SCOPES.length === 0) {
            return;
        }
        STORE.set(SCOPE);
    });
    /* eslint-disable sort-keys, arrow-body-style */
    return {
        ...STORE,
        // SCOPE REGISTRATION
        registerScope: (scope, defaults = {}) => {
            updateScope(STORE, getScope(STORE, scope) ?? defaults, scope);
            registerScope(SCOPE_LIST, scope);
        },
        unregisterScope: (scope) => {
            unregisterScope(SCOPE_LIST, scope);
        },
        // PARAMETERS
        getParameter: (key, scope) => {
            return getParameter(STORE, key, scope);
        },
        removeParameter: (key, scope) => {
            removeParameter(STORE, key, scope);
        },
        setParameter: (key, value, scope) => {
            setParameter(STORE, key, value, scope);
        },
        subscribeParameter: (key, callback, scope) => {
            return subscribeParameter(STORE, key, callback, scope);
        },
        // SCOPES
        getScope: (scope) => {
            return getScope(STORE, scope);
        },
        updateScope: (data, scope) => {
            updateScope(STORE, data, scope);
        },
        subscribeScope: (scope, callback) => {
            return subscribeScope(STORE, scope, callback);
        },
    };
    /* eslint-enable */
};
export const QUERY_STATE = scopedQueryState();
/* eslint-enable */ 
